import { getWagmiConfig } from "./wallet/reown.js";
import { getGalleryView } from "./gallerySwitcher.js";
import { envHasStakingContracts, getStakingEnv } from "./lib/env.js";
import {
  fetchStakedTokenIds,
  fetchXpBalanceWei,
  formatOwxp,
  readStakingPaused,
  stakingAddressesOrNull,
} from "./lib/stakingData.js";
import {
  clearRoostSelection,
  syncRoostGallery,
  getSelectedRoostTokenIds,
} from "./roostGallery.js";
import { getSelectedWalletTokenIds, syncNftGallery } from "./nftGallery.js";
import { runStake, runUnstake, runClaim } from "./stakingTx.js";

/** @type {{ paused: boolean }} */
let lastChainState = { paused: true };
/** @type {`0x${string}` | null} */
let lastDashboardAddress = null;
let roostRefreshSeq = 0;

function formatSelectedHint(ids) {
  if (ids.length === 0) return "";
  const labels = ids.map((id) => `#${id}`);
  if (labels.length <= 3) return `Selected · ${labels.join(", ")}`;
  return `Selected · ${labels.slice(0, 2).join(", ")}, +${labels.length - 2} more`;
}

export function syncGalleryHint() {
  const el = document.getElementById("staking-selection-hint");
  if (!el) return;
  const view = getGalleryView();
  if (view === "wallet") {
    const ids = getSelectedWalletTokenIds();
    const hint = formatSelectedHint(ids);
    el.textContent = hint;
    el.hidden = hint === "";
  } else {
    const ids = getSelectedRoostTokenIds();
    const hint = formatSelectedHint(ids);
    el.textContent = hint;
    el.hidden = hint === "";
  }
}

export function syncStakeButtonsState() {
  const stakeBtn = document.getElementById("btn-stake");
  const unstakeBtn = document.getElementById("btn-unstake");
  const env = getStakingEnv();
  const ok = envHasStakingContracts(env);
  const walletIds = getSelectedWalletTokenIds();
  const roostIds = getSelectedRoostTokenIds();
  const view = getGalleryView();

  const { paused } = lastChainState;
  const claimBtn = document.getElementById("btn-claim");

  const stakeAllowed = walletIds.length > 0;

  let claimAllowed = roostIds.length > 0;
  if (claimAllowed) {
    for (const id of roostIds) {
      const card = document.querySelector(
        `.nft-card--roost[data-token-id="${id}"]`
      );
      if (card?.getAttribute("data-can-claim") !== "true") {
        claimAllowed = false;
        break;
      }
    }
  }

  const deactivateAllowed = roostIds.length > 0;

  if (stakeBtn) {
    stakeBtn.disabled = !ok || !stakeAllowed || paused || view !== "wallet";
    stakeBtn.textContent =
      walletIds.length > 1 ? "Activate batch" : "Activate";
  }
  if (unstakeBtn) {
    unstakeBtn.disabled =
      !ok || !deactivateAllowed || paused || view !== "roost";
    unstakeBtn.textContent =
      roostIds.length > 1 ? "Deactivate batch" : "Deactivate";
  }
  if (claimBtn) {
    claimBtn.disabled = !ok || !claimAllowed || paused || view !== "roost";
    claimBtn.textContent = roostIds.length > 1 ? "Claim batch" : "Claim XP";
  }
  syncGalleryHint();
}

/**
 * Roost-only refresh path (used by manual refresh and when switching to Roost).
 * @param {{ force?: boolean }} [options]
 */
async function refreshRoostOnly(options = {}) {
  const address = lastDashboardAddress;
  const cfg = getWagmiConfig();
  const { staking } = stakingAddressesOrNull();
  const stakedEl = document.getElementById("stat-staked");
  const refreshBtn = document.getElementById("btn-roost-refresh");
  const seq = ++roostRefreshSeq;

  if (refreshBtn) refreshBtn.disabled = true;
  try {
    if (!address || !cfg || !staking) {
      await syncRoostGallery(
        cfg,
        address,
        "0x0000000000000000000000000000000000000000",
        []
      );
      if (!address && stakedEl) stakedEl.textContent = "—";
      syncStakeButtonsState();
      return;
    }
    const { tokenIds, stakeRows } = await fetchStakedTokenIds(cfg, staking, address, {
      force: options.force === true,
    });
    if (seq !== roostRefreshSeq) return;
    if (stakedEl) stakedEl.textContent = String(tokenIds.length);
    await syncRoostGallery(cfg, address, staking, tokenIds, { stakeRows });
    syncStakeButtonsState();
  } finally {
    if (seq === roostRefreshSeq && refreshBtn) refreshBtn.disabled = false;
  }
}

export async function refreshStakingDashboard(address) {
  lastDashboardAddress = address ?? null;
  const stakedEl = document.getElementById("stat-staked");
  const rewardsEl = document.getElementById("stat-rewards");
  const cfg = getWagmiConfig();
  const { staking, xp } = stakingAddressesOrNull();
  const zeroStaking = "0x0000000000000000000000000000000000000000";

  if (!address) {
    if (stakedEl) stakedEl.textContent = "—";
    if (rewardsEl) rewardsEl.textContent = "—";
    clearRoostSelection();
    lastChainState = { paused: true };
    await syncNftGallery(null);
    await syncRoostGallery(cfg, null, zeroStaking, []);
    syncStakeButtonsState();
    return;
  }

  if (!cfg) {
    if (stakedEl) stakedEl.textContent = "—";
    if (rewardsEl) rewardsEl.textContent = "—";
    clearRoostSelection();
    lastChainState = { paused: true };
    await syncRoostGallery(cfg, null, zeroStaking, []);
    syncStakeButtonsState();
    return;
  }

  if (!staking || !xp) {
    if (stakedEl) stakedEl.textContent = "—";
    if (rewardsEl) rewardsEl.textContent = "—";
    clearRoostSelection();
    lastChainState = { paused: true };
    await syncNftGallery(address);
    await syncRoostGallery(cfg, address, zeroStaking, []);
    syncStakeButtonsState();
    return;
  }

  const [xpWei, roost, paused] = await Promise.all([
    fetchXpBalanceWei(cfg, xp, address),
    fetchStakedTokenIds(cfg, staking, address),
    readStakingPaused(cfg, staking),
  ]);
  const { tokenIds, stakeRows } = roost;

  lastChainState = { paused };

  if (stakedEl) stakedEl.textContent = String(tokenIds.length);
  if (rewardsEl) {
    const s = formatOwxp(xpWei);
    const n = Number.parseFloat(s);
    rewardsEl.textContent = Number.isFinite(n)
      ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : s;
  }

  await syncNftGallery(address, { excludeTokenIds: tokenIds });
  await syncRoostGallery(cfg, address, staking, tokenIds, { stakeRows });
  syncStakeButtonsState();
}

export function bindStakingActions() {
  document
    .getElementById("btn-stake")
    ?.addEventListener("click", () => void runStake());
  document
    .getElementById("btn-unstake")
    ?.addEventListener("click", () => void runUnstake());
  document
    .getElementById("btn-claim")
    ?.addEventListener("click", () => void runClaim());
  document
    .getElementById("btn-roost-refresh")
    ?.addEventListener("click", () => void refreshRoostOnly({ force: true }));
  window.addEventListener("owlex:gallery-view", (e) => {
    if (e.detail?.view === "roost") {
      // Respect roost cache; manual ↻ uses force:true.
      void refreshRoostOnly({ force: false });
    }
  });
}
